import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactElement } from 'react';
import { FANGLINIEN, FANG_PIXEL, fangeGroesse, fangeKasten, type Fanglinie } from '@schnitt/shared';
import { markenKasten, markenVersatz, transformAt } from '@schnitt/shared';
import type { Clip, Op } from '@schnitt/shared';
import { useEditor } from '../store';

/**
 * Grab handles on the canvas.
 *
 * Position, size and rotation were numbers in a form; now the layer is dragged
 * where it belongs. The frame is measured from the element that is actually
 * drawn (`data-clip`), so it fits a line of text as closely as it fits a
 * picture, whatever the layer turns out to be.
 *
 * While a drag is in progress the change is local and immediate. One operation
 * goes out when the finger lifts: fifty over a websocket would be fifty
 * revisions and fifty steps of undo for one movement.
 */

type Mode = 'move' | 'scale' | 'rotate';

export interface Draft {
  clipId: string;
  x: number;
  y: number;
  scale: number;
  rotate: number;
}

interface Gesture extends Draft {
  mode: Mode;
  pointerId: number;
  /** Canvas box at the moment the gesture started, in CSS pixels. */
  canvas: { left: number; top: number; width: number; height: number };
  startX: number;
  startY: number;
  /** Distance and angle from the layer centre when the gesture started. */
  startDistance: number;
  startAngle: number;
  from: Draft;
}

/** Die Lage einer Form in ihrer Ebene (7.82b), wie sie im Schnitt steht. */
interface FormLage {
  x: number;
  y: number;
  groesse: number;
  drehung: number;
}

interface FormZug {
  mode: Mode;
  pointerId: number;
  startX: number;
  startY: number;
  /** Die Mitte der Form auf dem Schirm, als der Zug anfing. */
  mitteX: number;
  mitteY: number;
  startDistance: number;
  startAngle: number;
  /** Die ungedrehte Grösse der Ebene und ihr Winkel, in Bildpunkten. */
  breite: number;
  hoehe: number;
  winkel: number;
  from: FormLage;
}

const MIN_SCALE = 0.05;
const MAX_SCALE = 4;
/** So klein wird ein Rahmen nie: eine Fingerkuppe muss hineinpassen. */
const MIN_RAHMEN = 28;

/**
 * Snap a rotation to the quarter turns, so "straight" is reachable by hand.
 *
 * Gerechnet wird in −180…180, nicht in 0…360: eine kleine Drehung gegen den
 * Uhrzeigersinn ist dann −3° und nicht 357°. Der Unterschied ist nicht
 * kosmetisch -- zwischen einem Keyframe bei 0° und einem bei 357° nimmt jede
 * geradlinige Rechnung den langen Weg, und die Ebene dreht sich einmal um
 * sich selbst.
 */
function snapRotation(degrees: number): number {
  const wrapped = ((((degrees % 360) + 540) % 360)) - 180;
  for (const anchor of [-180, -90, 0, 90, 180]) {
    if (Math.abs(wrapped - anchor) <= 4) return anchor === 180 ? -180 : anchor;
  }
  return Math.round(wrapped);
}

export function CanvasHandles({ clip, canvas }: { clip: Clip; canvas: HTMLElement | null }): ReactElement | null {
  const dispatch = useEditor((s) => s.dispatch);
  const playhead = useEditor((s) => s.playhead);
  const fps = useEditor((s) => s.timeline.settings.fps);
  // With keyframes the frame follows the animated position, and a drag
  // writes the keyframe under the playhead instead of the static transform.
  const local = playhead - clip.start;
  const shown = transformAt(clip, local);
  const role = useEditor((s) => s.role);
  const griff = useEditor((s) => s.griff);
  const setGriff = useEditor((s) => s.setGriff);
  /**
   * Der Rahmen: Lage und Grösse der Ebene *ungedreht*, dazu ihre Hülle (die
   * achsenparallele Kiste um die gedrehte Ebene) für die Magnete.
   */
  const [box, setBox] = useState<{ left: number; top: number; width: number; height: number; huelleB: number; huelleH: number } | null>(null);
  /** Der Winkel, in dem die Ebene gerade gezeichnet ist -- für das Messen. */
  const winkel = useRef(0);
  winkel.current = shown.rotate;
  const [draft, setDraft] = useState<Draft | null>(null);
  /** Woran die Ebene gerade hängt -- gezeichnet wird nur, was wirklich fängt. */
  const [fang, setFang] = useState<{ x: Fanglinie | null; y: Fanglinie | null }>({ x: null, y: null });
  const gesture = useRef<Gesture | null>(null);
  const wurzel = useRef<HTMLDivElement | null>(null);

  // **Die Form hat eigene Anfasser** (7.224; Sascha, 4. Oktober: „die
  // Gedankenblase kann doch bestimmt anfasser auf der Leinwand bekommen“).
  // Ein Knopf „Form“ am Rahmen schaltet um: dann gehören Schieben, Ecken und
  // Drehgriff der Form und nicht der Ebene -- zwei Rahmen übereinander wären
  // ein Glücksspiel, welcher den Finger bekommt.
  const maske = clip.maske && clip.maske.form !== 'keine' ? clip.maske : null;
  const [formModus, setFormModus] = useState(false);
  const setMaskeEntwurf = useEditor((s) => s.setMaskeEntwurf);
  const formEntwurf = useEditor((s) => (s.maskeEntwurf?.clipId === clip.id ? s.maskeEntwurf : null));
  const formZug = useRef<FormZug | null>(null);
  useEffect(() => {
    setFormModus(false);
  }, [clip.id]);

  // Eine Bauchbinde steht mit der Mitte ihres Inhalts neben x/y, nicht mit
  // der des Bildes (7.19a). Der Rahmen, das Drehen und die Magnete meinen
  // diese Mitte; gespeichert wird weiter x/y.
  const brandKit = useEditor((s) => s.project?.brandKit);
  const seiten = useEditor((s) => (s.timeline.settings.height > 0 ? s.timeline.settings.width / s.timeline.settings.height : 1));
  const tpl =
    clip.source.kind === 'brand'
      ? brandKit?.templates.find((t) => t.id === (clip.source as { templateId: string }).templateId)
      : undefined;
  const { dx: markeDx, dy: markeDy } =
    tpl && clip.source.kind === 'brand'
      ? markenVersatz(markenKasten(tpl, clip.source.fields, seiten, Boolean(brandKit?.logoPath)))
      : { dx: 0, dy: 0 };

  const current: Draft = draft ?? {
    clipId: clip.id,
    x: shown.x,
    y: shown.y,
    scale: shown.scale,
    rotate: shown.rotate,
  };

  /**
   * Der Anfang eines Zuges -- egal, ob der Finger auf einem Anfasser aufkam
   * oder auf der Leinwand und erst hier ankommt.
   */
  const beginne = (mode: Mode, clientX: number, clientY: number, pointerId: number): void => {
    if (!canvas) return;
    const rect = innenflaeche(canvas);
    const centreX = rect.left + (current.x + markeDx) * rect.width;
    const centreY = rect.top + (current.y + markeDy) * rect.height;
    const dx = clientX - centreX;
    const dy = clientY - centreY;
    gesture.current = {
      ...current,
      mode,
      pointerId,
      canvas: rect,
      startX: clientX,
      startY: clientY,
      startDistance: Math.max(1, Math.hypot(dx, dy)),
      startAngle: (Math.atan2(dy, dx) * 180) / Math.PI,
      from: current,
    };
    setDraft(current);
  };

  // Follow the drawn element: it moves when the layer does, and it changes
  // size when the text does.
  useEffect(() => {
    if (!canvas) return;
    let frame = 0;
    const measure = () => {
      const element = canvas.querySelector<HTMLElement>(`[data-clip="${CSS.escape(clip.id)}"]`);
      const outer = innenflaeche(canvas);
      if (!element) {
        setBox(null);
        return;
      }
      const inner = element.getBoundingClientRect();
      // Gedreht ist das, was der Browser misst, die Hülle der Ebene -- und
      // die drehte der Rahmen bis zum 29. September ein zweites Mal: um eine
      // halb so grosse, um 30° gedrehte Fläche lag ein Rahmen, der über die
      // Leinwand hinausragte. Die ungedrehte Grösse kommt aus dem
      // Seitenverhältnis der Ebene (`offsetWidth`/`offsetHeight`, vor jeder
      // Verwandlung) und dem Mass, mit dem die Hülle darauf passt; die Mitte
      // bleibt beim Drehen, wo sie ist.
      const a = (winkel.current * Math.PI) / 180;
      const c = Math.abs(Math.cos(a));
      const sn = Math.abs(Math.sin(a));
      const ow = element.offsetWidth;
      const oh = element.offsetHeight;
      let width = inner.width;
      let height = inner.height;
      if (sn > 1e-6 && ow > 0 && oh > 0) {
        const k = (inner.width / (ow * c + oh * sn) + inner.height / (ow * sn + oh * c)) / 2;
        width = ow * k;
        height = oh * k;
      }
      const mx = inner.left + inner.width / 2 - outer.left;
      const my = inner.top + inner.height / 2 - outer.top;
      setBox({
        left: mx - width / 2,
        top: my - height / 2,
        width,
        height,
        huelleB: inner.width,
        huelleH: inner.height,
      });
    };
    const loop = () => {
      measure();
      frame = requestAnimationFrame(loop);
    };
    frame = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frame);
  }, [canvas, clip.id]);

  /**
   * Den Griff aufnehmen, den die Leinwand angefangen hat (7.6a).
   *
   * `setPointerCapture` verlangt nicht, dass das `pointerdown` hier ankam --
   * nur, dass der Finger noch unten ist. War er schneller weg, als dieser
   * Effekt läuft, wirft es, und dann war es eben ein Tipp.
   */
  useEffect(() => {
    if (!griff || griff.clipId !== clip.id) return;
    const el = wurzel.current;
    if (!el || !canvas) return;
    setGriff(null);
    try {
      el.setPointerCapture(griff.pointerId);
    } catch {
      return;
    }
    beginne('move', griff.x, griff.y, griff.pointerId);
    // `beginne` steht bei jedem Bild neu da; in den Abhängigkeiten wäre das
    // eine Schleife. `box`, weil es den Rahmen erst gibt, wenn er gemessen ist.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [griff, clip.id, canvas, box]);

  if (!canvas || !box || role === 'viewer') return null;

  const begin = (mode: Mode) => (event: ReactPointerEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    (event.target as HTMLElement).setPointerCapture(event.pointerId);
    beginne(mode, event.clientX, event.clientY, event.pointerId);
  };

  const move = (event: ReactPointerEvent<HTMLElement>) => {
    const g = gesture.current;
    if (!g || g.pointerId !== event.pointerId) return;
    event.preventDefault();
    const centreX = g.canvas.left + (g.from.x + markeDx) * g.canvas.width;
    const centreY = g.canvas.top + (g.from.y + markeDy) * g.canvas.height;

    if (g.mode === 'move') {
      const rohX = clamp(((g.from.x + markeDx) * g.canvas.width + (event.clientX - g.startX)) / g.canvas.width, 0, 1);
      const rohY = clamp(((g.from.y + markeDy) * g.canvas.height + (event.clientY - g.startY)) / g.canvas.height, 0, 1);
      // Magnete (7.56). Die Ausdehnung der Ebene kommt aus dem gemessenen
      // Rahmen: nur so rastet die *Kante* ein und nicht bloß die Mitte -- wer
      // eine Bauchbinde an den Schutzbereich setzt, meint ihre Unterkante.
      const breite = g.canvas.width > 0 ? (box?.huelleB ?? 0) / g.canvas.width : 0;
      const hoehe = g.canvas.height > 0 ? (box?.huelleH ?? 0) / g.canvas.height : 0;
      const gefangen = fangeKasten(
        rohX,
        rohY,
        breite,
        hoehe,
        FANG_PIXEL / Math.max(1, g.canvas.width),
        FANG_PIXEL / Math.max(1, g.canvas.height),
      );
      setFang(gefangen.linien);
      setDraft({ ...g.from, x: gefangen.x - markeDx, y: gefangen.y - markeDy });
      return;
    }
    const distance = Math.max(1, Math.hypot(event.clientX - centreX, event.clientY - centreY));
    if (g.mode === 'scale') {
      const roh = clamp((g.from.scale * distance) / g.startDistance, MIN_SCALE, MAX_SCALE);
      // Magnete auch am Anfasser (7.56b): dieselben sieben Linien, nur rastet
      // hier die Kante über die *Größe* ein statt über die Lage. Die
      // Ausdehnung wird auf Größe 1 zurückgerechnet -- gemessen ist der
      // Rahmen bei der Größe, mit der der Zug anfing.
      const grund = (mass: number, achse: number): number =>
        achse > 0 && g.from.scale > 0 ? mass / achse / g.from.scale : 0;
      const gefangen = fangeGroesse(roh, {
        x: g.from.x + markeDx,
        y: g.from.y + markeDy,
        breite: grund(box?.huelleB ?? 0, g.canvas.width),
        hoehe: grund(box?.huelleH ?? 0, g.canvas.height),
        toleranzX: FANG_PIXEL / Math.max(1, g.canvas.width),
        toleranzY: FANG_PIXEL / Math.max(1, g.canvas.height),
      });
      setFang(gefangen.linien);
      setDraft({ ...g.from, scale: clamp(gefangen.groesse, MIN_SCALE, MAX_SCALE) });
      return;
    }
    const angle = (Math.atan2(event.clientY - centreY, event.clientX - centreX) * 180) / Math.PI;
    setDraft({ ...g.from, rotate: snapRotation(g.from.rotate + (angle - g.startAngle)) });
  };

  const end = (event: ReactPointerEvent<HTMLElement>) => {
    const g = gesture.current;
    if (!g || g.pointerId !== event.pointerId) return;
    gesture.current = null;
    const next = draft;
    setDraft(null);
    setFang({ x: null, y: null });
    if (!next) return;
    const patch: Record<string, number> = {};
    if (next.x !== g.from.x || next.y !== g.from.y) {
      patch.x = round(next.x);
      patch.y = round(next.y);
    }
    if (next.scale !== g.from.scale) patch.scale = round(next.scale);
    if (next.rotate !== g.from.rotate) patch.rotate = Math.round(next.rotate);
    // A tap that moved nothing is a tap, not an edit.
    if (Object.keys(patch).length === 0) return;
    if (clip.keyframes.length > 0) {
      // Update the keyframe under the playhead, or set a new one there.
      const transform = { ...shown, ...patch };
      const near = clip.keyframes.find((k) => Math.abs(k.at - local) < 1 / fps);
      const keyframes = near
        ? clip.keyframes.map((k) => (k === near ? { ...k, transform } : k))
        : [...clip.keyframes, { at: Math.max(0, local), transform }];
      dispatch({ op: 'updateClip', clipId: clip.id, patch: { keyframes } } as Op);
      return;
    }
    dispatch({ op: 'updateClip', clipId: clip.id, patch: { transform: { ...clip.transform, ...patch } } } as Op);
  };

  if (maske && formModus) {
    // Wo die Form auf dem Schirm steht: ihre Mitte (x, y) in Anteilen der
    // ungedrehten Ebene, mit der Ebene gedreht; ihre Grösse `groesse` mal die
    // Ebene; ihr Winkel der der Ebene plus ihr eigener -- dieselbe Rechnung
    // wie `insFeld`, nur vorwärts.
    const lage: FormLage = formEntwurf ?? { x: maske.x, y: maske.y, groesse: maske.groesse, drehung: maske.drehung };
    const lw = (shown.rotate * Math.PI) / 180;
    const ox = (lage.x - 0.5) * box.width;
    const oy = (lage.y - 0.5) * box.height;
    const fx = box.left + box.width / 2 + ox * Math.cos(lw) - oy * Math.sin(lw);
    const fy = box.top + box.height / 2 + ox * Math.sin(lw) + oy * Math.cos(lw);
    const fw = Math.max(MIN_RAHMEN, lage.groesse * box.width);
    const fh = Math.max(MIN_RAHMEN, lage.groesse * box.height);

    const formBeginne = (mode: Mode) => (event: ReactPointerEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      (event.target as HTMLElement).setPointerCapture(event.pointerId);
      const r = innenflaeche(canvas);
      const mitteX = r.left + fx;
      const mitteY = r.top + fy;
      const dx = event.clientX - mitteX;
      const dy = event.clientY - mitteY;
      formZug.current = {
        mode,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        mitteX,
        mitteY,
        startDistance: Math.max(1, Math.hypot(dx, dy)),
        startAngle: (Math.atan2(dy, dx) * 180) / Math.PI,
        breite: Math.max(1, box.width),
        hoehe: Math.max(1, box.height),
        winkel: lw,
        from: lage,
      };
      setMaskeEntwurf({ clipId: clip.id, ...lage });
    };
    const formZiehe = (event: ReactPointerEvent<HTMLElement>) => {
      const z = formZug.current;
      if (!z || z.pointerId !== event.pointerId) return;
      event.preventDefault();
      if (z.mode === 'move') {
        // Der Weg auf dem Schirm, zurückgedreht in die Ebene.
        const dx = event.clientX - z.startX;
        const dy = event.clientY - z.startY;
        const lx = dx * Math.cos(z.winkel) + dy * Math.sin(z.winkel);
        const ly = -dx * Math.sin(z.winkel) + dy * Math.cos(z.winkel);
        setMaskeEntwurf({
          clipId: clip.id,
          ...z.from,
          x: clamp(z.from.x + lx / z.breite, -1, 2),
          y: clamp(z.from.y + ly / z.hoehe, -1, 2),
        });
        return;
      }
      const dx = event.clientX - z.mitteX;
      const dy = event.clientY - z.mitteY;
      if (z.mode === 'scale') {
        const groesse = clamp((z.from.groesse * Math.max(1, Math.hypot(dx, dy))) / z.startDistance, MIN_SCALE, MAX_SCALE);
        setMaskeEntwurf({ clipId: clip.id, ...z.from, groesse });
        return;
      }
      const winkel = (Math.atan2(dy, dx) * 180) / Math.PI;
      setMaskeEntwurf({ clipId: clip.id, ...z.from, drehung: snapRotation(z.from.drehung + (winkel - z.startAngle)) });
    };
    const formEnde = (event: ReactPointerEvent<HTMLElement>) => {
      const z = formZug.current;
      if (!z || z.pointerId !== event.pointerId) return;
      formZug.current = null;
      const neu = useEditor.getState().maskeEntwurf;
      setMaskeEntwurf(null);
      if (!neu || neu.clipId !== clip.id) return;
      const fertig = { x: round(neu.x), y: round(neu.y), groesse: round(neu.groesse), drehung: Math.round(neu.drehung) };
      if (fertig.x === z.from.x && fertig.y === z.from.y && fertig.groesse === z.from.groesse && fertig.drehung === z.from.drehung) return;
      dispatch({ op: 'updateClip', clipId: clip.id, patch: { maske: { ...maske, ...fertig } } } as Op);
    };
    // Ragt der Rahmen über die Leinwand, wird abgeschnitten, was übersteht --
    // dann stehen die Anfasser innen (`.innen`). Gerechnet an der Hülle des
    // gedrehten Rahmens, mit dem Drehgriff darüber.
    const fw0 = lage.groesse * box.width;
    const fh0 = lage.groesse * box.height;
    const fwinkel = lw + (lage.drehung * Math.PI) / 180;
    const huelleB = Math.abs(fw0 * Math.cos(fwinkel)) + Math.abs(fh0 * Math.sin(fwinkel));
    const huelleH = Math.abs(fw0 * Math.sin(fwinkel)) + Math.abs(fh0 * Math.cos(fwinkel));
    const flaeche = innenflaeche(canvas);
    const innen =
      fx - huelleB / 2 < 16 ||
      fx + huelleB / 2 > flaeche.width - 16 ||
      fy - huelleH / 2 < 42 ||
      fy + huelleH / 2 > flaeche.height - 56;
    return (
      <div
        className={`handles form-rahmen${innen ? ' innen' : ''}`}
        data-form-rahmen
        style={{
          left: fx - fw / 2,
          top: fy - fh / 2,
          width: fw,
          height: fh,
          transform: `rotate(${shown.rotate + lage.drehung}deg)`,
        }}
        onPointerDown={formBeginne('move')}
        onPointerMove={formZiehe}
        onPointerUp={formEnde}
        onPointerCancel={formEnde}
      >
        {(['nw', 'ne', 'se', 'sw'] as const).map((corner) => (
          <span
            key={corner}
            className={`handle ${corner}`}
            data-form-ecke={corner}
            onPointerDown={formBeginne('scale')}
            onPointerMove={formZiehe}
            onPointerUp={formEnde}
            onPointerCancel={formEnde}
          />
        ))}
        <span
          className="handle rotate"
          data-form-drehen
          onPointerDown={formBeginne('rotate')}
          onPointerMove={formZiehe}
          onPointerUp={formEnde}
          onPointerCancel={formEnde}
        />
        <button
          type="button"
          className="form-knopf"
          data-form-fertig
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            setFormModus(false);
          }}
        >
          Fertig
        </button>
      </div>
    );
  }

  // While dragging, the frame follows the finger rather than the (not yet
  // saved) layer, so it does not lag a frame behind.
  const rect = innenflaeche(canvas);
  const dragging = draft !== null;
  const left = dragging ? (current.x + markeDx) * rect.width - box.width / 2 : box.left;
  const top = dragging ? (current.y + markeDy) * rect.height - box.height / 2 : box.top;
  // Ein Rahmen, der kleiner als eine Fingerkuppe wäre, wächst -- und zwar um
  // die Mitte der Ebene. Vorher wuchs er über `min-height` nach unten, und
  // um eine flache Bauchbinde am kleinen Schirm lag er schief (7.19a).
  const width = Math.max(MIN_RAHMEN, box.width * (dragging ? current.scale / shown.scale : 1));
  const height = Math.max(MIN_RAHMEN, box.height * (dragging ? current.scale / shown.scale : 1));
  const zuBreit = width - box.width * (dragging ? current.scale / shown.scale : 1);
  const zuHoch = height - box.height * (dragging ? current.scale / shown.scale : 1);

  return (
    <>
      {/* Das Raster gibt es nur, solange gezogen wird: eine Leinwand mit
          Hilfslinien darauf ist keine Vorschau mehr. Die Linie, an der die
          Ebene gerade hängt, ist die helle -- man sieht, *warum* sie stehen
          geblieben ist. */}
      {dragging ? (
        <div className="fang-raster" aria-hidden="true" data-fang-raster>
          {FANGLINIEN.map((linie) => (
            <span key={`x-${linie.bei}`} className={`fang-linie senkrecht ${linie.art}`} style={{ left: `${linie.bei * 100}%` }} />
          ))}
          {FANGLINIEN.map((linie) => (
            <span key={`y-${linie.bei}`} className={`fang-linie waagerecht ${linie.art}`} style={{ top: `${linie.bei * 100}%` }} />
          ))}
          {fang.x ? (
            <span className="fang-linie senkrecht faengt" style={{ left: `${fang.x.bei * 100}%` }} data-fang="x" data-fang-art={fang.x.art} />
          ) : null}
          {fang.y ? (
            <span className="fang-linie waagerecht faengt" style={{ top: `${fang.y.bei * 100}%` }} data-fang="y" data-fang-art={fang.y.art} />
          ) : null}
        </div>
      ) : null}
    <div
      ref={wurzel}
      // Ein kleiner Rahmen (7.19a: ein Logo allein ist oft nur ein
      // Fingerbreit gross) bekäme sonst seine Mitte von den vier Ecken
      // zugedeckt -- wer schieben wollte, zog an einer Ecke.
      className={`handles${Math.min(width, height) < 64 ? ' klein' : ''}`}
      style={{
        left: dragging ? (current.x + markeDx) * rect.width - width / 2 : left - zuBreit / 2,
        top: dragging ? (current.y + markeDy) * rect.height - height / 2 : top - zuHoch / 2,
        width,
        height,
        transform: `rotate(${current.rotate}deg)`,
      }}
      onPointerDown={begin('move')}
      onPointerMove={move}
      onPointerUp={end}
      onPointerCancel={end}
    >
      {(['nw', 'ne', 'se', 'sw'] as const).map((corner) => (
        <span
          key={corner}
          className={`handle ${corner}`}
          onPointerDown={begin('scale')}
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={end}
        />
      ))}
      <span
        className="handle rotate"
        onPointerDown={begin('rotate')}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
      />
      {maske && !dragging ? (
        <button
          type="button"
          className="form-knopf"
          data-form-anfasser
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            setFormModus(true);
          }}
        >
          Form
        </button>
      ) : null}
    </div>
    </>
  );
}

/**
 * Die Fläche, in der die Ebenen liegen: die Leinwand **ohne ihren Rand**.
 *
 * `.canvas` hat einen Haarstrich von einem Punkt. Die Ebenen und der Rahmen
 * der Anfasser liegen darin (`inset: 0` gilt ab der Innenkante); gemessen
 * wurde aber an der Aussenkante -- der Rahmen stand deshalb um genau diesen
 * Punkt nach rechts unten versetzt, und Ziehen und Magnete rechneten mit
 * einer Leinwand, die zwei Punkte zu breit war.
 */
function innenflaeche(canvas: HTMLElement): { left: number; top: number; width: number; height: number } {
  const r = canvas.getBoundingClientRect();
  return { left: r.left + canvas.clientLeft, top: r.top + canvas.clientTop, width: canvas.clientWidth, height: canvas.clientHeight };
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

function round(value: number): number {
  return Math.round(value * 1000) / 1000;
}
